import { useId } from 'react';
function SectionHeading({ title, description, id, required }) {
  return (
    <div className="tcf-section-heading">
      <h3 className="tcf-eyebrow" id={id}>
        {title}
        {required && (
          <span className="tcf-required" aria-hidden="true">
            *
          </span>
        )}
      </h3>
      {description && <p className="tcf-section-desc">{description}</p>}
    </div>
  );
}
function FieldShell({ label, htmlFor, required, error, children, className }) {
  return (
    <div className={['tcf-field', error ? 'tcf-field--error' : '', className ?? ''].join(' ').trim()}>
      <label className="tcf-label" htmlFor={htmlFor}>
        {label}
        {required && (
          <span className="tcf-required" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      {error && (
        <p className="tcf-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
function TextField({ label, error, required, wrapperClassName, ...rest }) {
  const id = useId();
  return (
    <FieldShell label={label} htmlFor={id} required={required} error={error} className={wrapperClassName}>
      <input
        id={id}
        className="tcf-input"
        aria-required={required || void 0}
        aria-invalid={error ? true : void 0}
        {...rest}
      />
    </FieldShell>
  );
}
function SelectField({ label, error, required, options, wrapperClassName, ...rest }) {
  const id = useId();
  return (
    <FieldShell label={label} htmlFor={id} required={required} error={error} className={wrapperClassName}>
      <div className="tcf-select-wrap">
        <select
          id={id}
          className="tcf-input tcf-select"
          aria-required={required || void 0}
          aria-invalid={error ? true : void 0}
          {...rest}
        >
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <ChevronIcon />
      </div>
    </FieldShell>
  );
}
function RadioGroup({ name, legend, legendHidden, required, options, value, onChange, error, renderExtra }) {
  const baseId = useId();
  return (
    <fieldset
      className={['tcf-field tcf-radio-group', error ? 'tcf-field--error' : ''].join(' ').trim()}
      aria-invalid={error ? true : void 0}
    >
      <legend className={legendHidden ? 'tcf-visually-hidden' : 'tcf-label'}>
        {legend}
        {required && !legendHidden && (
          <span className="tcf-required" aria-hidden="true">
            *
          </span>
        )}
      </legend>
      <div className="tcf-choices">
        {options.map((opt, i) => {
          const id = `${baseId}-${i}`;
          const checked = value === opt.value;
          const extra = checked && renderExtra ? renderExtra(opt.value) : null;
          return (
            <div className={['tcf-card', checked ? 'tcf-card--checked' : ''].join(' ').trim()} key={opt.value}>
              <label htmlFor={id} className="tcf-card-row">
                <input
                  type="radio"
                  id={id}
                  name={name}
                  value={opt.value}
                  checked={checked}
                  onChange={() => onChange(opt.value)}
                  className="tcf-radio"
                />
                <span className="tcf-card-dot" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.7 7.2c-.4-.4-1-.4-1.4 0l-7.5 7.5-3.1-3.1c-.4-.4-1-.4-1.4 0s-.4 1 0 1.4l3.8 3.8c.2.2.4.3.7.3s.5-.1.7-.3l8.2-8.2c.4-.4.4-1 0-1.4" />
                  </svg>
                </span>
                <span className="tcf-choice-label">{opt.label}</span>
              </label>
              {extra && <div className="tcf-card-extra">{extra}</div>}
            </div>
          );
        })}
      </div>
      {error && (
        <p className="tcf-error" role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}
function InfoField({ label, value }) {
  return (
    <div className="tcf-field tcf-info-field">
      <span className="tcf-label">{label}</span>
      <p className="tcf-info-value">{value}</p>
    </div>
  );
}
function ChevronIcon() {
  return (
    <svg className="tcf-select-chevron" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function RefreshIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 11A8 8 0 0 0 6.34 6.34L4 8.7M4 13a8 8 0 0 0 13.66 4.66L20 15.3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 4v4.7h4.7M20 20v-4.7h-4.7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
export { InfoField, RadioGroup, RefreshIcon, SectionHeading, SelectField, TextField };
